import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Toast, type ToastVariant } from '../components/Toast';

interface Props { user: AuthUser }

interface Bulletin {
  id: string; title_ms: string; title_en: string | null;
  content_ms: string; content_en: string | null;
  target_school_ids: string[] | null; target_count: string;
  issued_by: string; issued_at: string; attachment_url: string | null;
}

const URL_RE = /^https?:\/\/.+/i;

export function DPEBulletinsPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  const [bulletins,  setBulletins]  = useState<Bulletin[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [toast,      setToast]      = useState<{ message: string; variant: ToastVariant } | null>(null);
  const [expanded,   setExpanded]   = useState<string | null>(null);

  const [showForm,   setShowForm]   = useState(false);
  const [form, setForm] = useState({
    titleMs: '', titleEn: '', contentMs: '', contentEn: '',
    targetSchoolIds: '', attachmentUrl: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving,    setSaving]    = useState(false);

  const isAdmin = user.roles.some((r) => ['admin', 'super_admin'].includes(r.role));

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await apiGet<Bulletin[]>('/api/dpe/bulletins');
      setBulletins(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralat memuatkan buletin. / Failed to load bulletins.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function validateForm(): string | null {
    if (!form.titleMs.trim())   return 'Tajuk (BM) diperlukan. / Title (BM) required.';
    if (form.titleMs.trim().length > 500) return 'Tajuk terlalu panjang (maks 500). / Title too long (max 500).';
    if (!form.contentMs.trim()) return 'Kandungan (BM) diperlukan. / Content (BM) required.';
    if (form.attachmentUrl.trim() && !URL_RE.test(form.attachmentUrl.trim()))
      return 'URL lampiran tidak sah (mesti bermula https://). / Invalid attachment URL (must start https://).';
    const ids = form.targetSchoolIds.split(',').map((s) => s.trim()).filter(Boolean);
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const bad = ids.find((id) => !UUID_RE.test(id));
    if (bad) return `ID sekolah tidak sah: "${bad}". / Invalid school ID: "${bad}".`;
    return null;
  }

  async function handleIssue() {
    const err = validateForm();
    if (err) { setFormError(err); return; }
    setSaving(true); setFormError(null);
    try {
      const ids = form.targetSchoolIds.split(',').map((s) => s.trim()).filter(Boolean);
      await apiPost('/api/dpe/bulletins', {
        titleMs:         form.titleMs.trim(),
        titleEn:         form.titleEn.trim() || undefined,
        contentMs:       form.contentMs.trim(),
        contentEn:       form.contentEn.trim() || undefined,
        targetSchoolIds: ids.length > 0 ? ids : undefined,
        attachmentUrl:   form.attachmentUrl.trim() || undefined,
      });
      setToast({ variant: 'success', message: 'Buletin berjaya dikeluarkan. / Bulletin issued successfully.' });
      setShowForm(false);
      setForm({ titleMs: '', titleEn: '', contentMs: '', contentEn: '', targetSchoolIds: '', attachmentUrl: '' });
      void load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Gagal mengeluarkan buletin. / Failed to issue bulletin.');
    } finally { setSaving(false); }
  }

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/admin/dpe" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>← {E ? 'DPE' : 'DPE'}</Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                📢 {E ? 'DPE Bulletins' : 'Buletin DPE'}
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                DPE Bulletins — {bulletins.length} buletin
              </p>
            </div>
            {isAdmin && !showForm && (
              <Button variant="primary" size="sm" onClick={() => { setShowForm(true); setFormError(null); }}>
                + Keluarkan Buletin
              </Button>
            )}
          </div>

          {/* Issue form */}
          {showForm && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg }}>
              <p style={{ margin: `0 0 ${spacing.lg}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                Keluarkan Buletin Baharu / Issue New Bulletin
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <LF label="Tajuk (BM) *" style={{ flex: '2 1 240px' }}>
                    <input type="text" value={form.titleMs} onChange={(e) => setForm((f) => ({ ...f, titleMs: e.target.value }))}
                      disabled={saving} style={inputSt()} maxLength={500} />
                  </LF>
                  <LF label="Title (EN)" style={{ flex: '2 1 240px' }}>
                    <input type="text" value={form.titleEn} onChange={(e) => setForm((f) => ({ ...f, titleEn: e.target.value }))}
                      disabled={saving} style={inputSt()} maxLength={500} />
                  </LF>
                </div>
                <LF label="Kandungan (BM) *">
                  <textarea value={form.contentMs} onChange={(e) => setForm((f) => ({ ...f, contentMs: e.target.value }))}
                    rows={5} disabled={saving} style={{ ...inputSt(), resize: 'vertical' }} />
                </LF>
                <LF label="Content (EN)">
                  <textarea value={form.contentEn} onChange={(e) => setForm((f) => ({ ...f, contentEn: e.target.value }))}
                    rows={5} disabled={saving} style={{ ...inputSt(), resize: 'vertical' }} />
                </LF>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <LF label="Sekolah Sasaran (UUID, pisahkan dengan koma — kosongkan untuk semua)" style={{ flex: '2 1 280px' }}>
                    <input type="text" value={form.targetSchoolIds} onChange={(e) => setForm((f) => ({ ...f, targetSchoolIds: e.target.value }))}
                      placeholder="UUID1, UUID2, … (atau kosongkan / leave blank for all)" disabled={saving} style={inputSt()} />
                  </LF>
                  <LF label="URL Lampiran / Attachment URL" style={{ flex: '2 1 200px' }}>
                    <input type="url" value={form.attachmentUrl} onChange={(e) => setForm((f) => ({ ...f, attachmentUrl: e.target.value }))}
                      placeholder="https://…" disabled={saving} style={inputSt()} maxLength={500} />
                  </LF>
                </div>
              </div>
              {formError && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{formError}</p>}
              <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
                <Button variant="primary" size="sm" onClick={() => void handleIssue()} disabled={saving} loading={saving}>
                  {saving ? 'Menghantar…' : 'Keluarkan / Issue'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setFormError(null); }} disabled={saving}>Batal</Button>
              </div>
            </div>
          )}

          {error && <ErrorBanner msg={error} onRetry={() => void load()} />}
          {loading && bulletins.length === 0 && <LoadingBox />}
          {!loading && !error && bulletins.length === 0 && (
            <EmptyBox icon="📢" title="Tiada Buletin" body="No bulletins have been issued yet." />
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, opacity: loading ? 0.7 : 1 }}>
            {bulletins.map((b) => {
              const isExpanded = expanded === b.id;
              const isAll = !b.target_school_ids || (Array.isArray(b.target_school_ids) && b.target_school_ids.length === 0);

              return (
                <div key={b.id} style={{
                  backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl,
                  border: `1px solid ${colors.neutral.gray800}`, overflow: 'hidden', boxShadow: shadows.lg,
                }}>
                  <div style={{ padding: spacing.lg }}>
                    <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Pill label={isAll ? 'Semua Sekolah' : `${b.target_count} Sekolah`} color={isAll ? '#a78bfa' : '#60a5fa'} />
                      <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                        {new Date(b.issued_at).toLocaleDateString('ms-BN', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{b.title_ms}</p>
                    {b.title_en && <p style={{ margin: `1px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{b.title_en}</p>}

                    {isExpanded && (
                      <div style={{ marginTop: spacing.sm }}>
                        <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: colors.neutral.gray200, whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{b.content_ms}</p>
                        {b.content_en && (
                          <p style={{ margin: `${spacing.sm} 0 0`, fontSize: typography.fontSize.sm, color: colors.neutral.gray400, whiteSpace: 'pre-wrap', lineHeight: '1.6', fontStyle: 'italic' }}>{b.content_en}</p>
                        )}
                      </div>
                    )}
                    {!isExpanded && (
                      <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                        {b.content_ms.length > 120 ? `${b.content_ms.slice(0, 120)}…` : b.content_ms}
                      </p>
                    )}

                    <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.sm, alignItems: 'center' }}>
                      <Button variant="ghost" size="sm" onClick={() => setExpanded(isExpanded ? null : b.id)}>
                        {isExpanded ? 'Sembunyikan ↑' : 'Baca Lebih ↓'}
                      </Button>
                      {b.attachment_url && (
                        <a href={b.attachment_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                          <Button variant="ghost" size="sm">📎 Lampiran</Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

function LF({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={style}><label style={labelSt}>{label}</label>{children}</div>;
}
function Pill({ label, color }: { label: string; color: string }) {
  return <span style={{ display: 'inline-block', padding: `1px ${spacing.xs}`, backgroundColor: `${color}22`, color, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>{label}</span>;
}
function ErrorBanner({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
      <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{msg}</p>
      <Button variant="ghost" size="sm" onClick={onRetry} style={{ marginTop: spacing.xs }}>Cuba Lagi</Button>
    </div>
  );
}
function LoadingBox() { return <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>; }
function EmptyBox({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: spacing.sm }}>{icon}</div>
      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{title}</p>
      <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>{body}</p>
    </div>
  );
}
const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return { width: '100%', backgroundColor: colors.neutral.black, border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md, color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`, fontSize: typography.fontSize.sm, outline: 'none', boxSizing: 'border-box', fontFamily: typography.fontFamily.body };
}
